import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Skeleton,
  Divider,
  Stack,
  Badge,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Extension as ModuleIcon,
  Tune as ParamIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Build as MaintenanceIcon,
  Archive as DeprecatedIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  FlashOn as CriticalIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Settings as SettingsIcon,
  BarChart as StatsIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import adminConfigService from '../../services/adminConfigService';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODULE_STATUS_CONFIG = {
  active:      { label: 'Actif',        color: 'success', icon: <ActiveIcon fontSize="small" /> },
  inactive:    { label: 'Inactif',      color: 'default', icon: <InactiveIcon fontSize="small" /> },
  maintenance: { label: 'Maintenance',  color: 'warning', icon: <MaintenanceIcon fontSize="small" /> },
  deprecated:  { label: 'Déprécié',     color: 'error',   icon: <DeprecatedIcon fontSize="small" /> }
};

const PARAM_STATUS_CONFIG = {
  active:     { label: 'Actif',        color: 'success' },
  inactive:   { label: 'Inactif',      color: 'default' },
  readonly:   { label: 'Lecture seule', color: 'info' },
  deprecated: { label: 'Déprécié',     color: 'error' }
};

const PARAM_PRIORITY_CONFIG = {
  critical: { label: 'Critique', color: 'error' },
  high:     { label: 'Haute',    color: 'warning' },
  medium:   { label: 'Moyenne',  color: 'info' },
  low:      { label: 'Basse',    color: 'default' }
};

const DATA_TYPE_CONFIG = {
  string:     { label: 'Texte',      color: '#6366f1' },
  integer:    { label: 'Entier',     color: '#0ea5e9' },
  float:      { label: 'Décimal',    color: '#06b6d4' },
  boolean:    { label: 'Booléen',    color: '#8b5cf6' },
  json:       { label: 'JSON',       color: '#f59e0b' },
  array:      { label: 'Tableau',    color: '#10b981' },
  percentage: { label: 'Pourcentage', color: '#ec4899' },
  currency:   { label: 'Devise',     color: '#22c55e' },
  duration:   { label: 'Durée',      color: '#f97316' }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const StatusChip = ({ status, type = 'module' }) => {
  const cfg = type === 'module' ? MODULE_STATUS_CONFIG[status] : PARAM_STATUS_CONFIG[status];
  if (!cfg) return null;
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
};

const PriorityChip = ({ priority }) => {
  const cfg = PARAM_PRIORITY_CONFIG[priority];
  if (!cfg) return null;
  return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
};

const DataTypeChip = ({ dataType }) => {
  const cfg = DATA_TYPE_CONFIG[dataType];
  if (!cfg) return <Chip label={dataType} size="small" />;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ backgroundColor: cfg.color, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
};

// ─── Stats Cards ──────────────────────────────────────────────────────────────

const StatsCards = ({ stats, loading }) => {
  const cards = [
    { label: 'Modules total', value: stats?.modules?.total ?? '-', color: '#6366f1', sub: `${stats?.modules?.active ?? 0} actifs` },
    { label: 'Paramètres total', value: stats?.parameters?.total ?? '-', color: '#0ea5e9', sub: `${stats?.parameters?.active ?? 0} actifs` },
    { label: 'Paramètres critiques', value: stats?.parameters?.critical ?? '-', color: '#ef4444', sub: 'Priorité critique' }
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((c) => (
        <Grid item xs={12} sm={4} key={c.label}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: c.color, width: 48, height: 48 }}>
                <StatsIcon />
              </Avatar>
              <Box>
                {loading ? (
                  <Skeleton width={60} height={32} />
                ) : (
                  <Typography variant="h5" fontWeight={700}>{c.value}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                <Typography variant="caption" color="text.disabled">{c.sub}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// ─── Module Form Dialog ───────────────────────────────────────────────────────

const initialModuleForm = {
  name: '', display_name: '', description: '', status: 'active', is_system: false, version: '1.0.0'
};

const ModuleFormDialog = ({ open, onClose, module: mod, onSaved }) => {
  const isEdit = Boolean(mod);
  const [form, setForm] = useState(initialModuleForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mod) {
      setForm({
        name: mod.name || '',
        display_name: mod.display_name || '',
        description: mod.description || '',
        status: mod.status || 'active',
        is_system: mod.is_system || false,
        version: mod.version || '1.0.0'
      });
    } else {
      setForm(initialModuleForm);
    }
    setErrors({});
  }, [mod, open]);

  const validate = () => {
    const e = {};
    if (!form.display_name.trim()) e.display_name = 'Nom d\'affichage requis';
    if (!isEdit && !form.name.trim()) e.name = 'Identifiant requis';
    if (!isEdit && !/^[a-z][a-z0-9_]*[a-z0-9]$/.test(form.name)) e.name = 'Format snake_case requis (ex: my_module)';
    if (!/^\d+\.\d+\.\d+$/.test(form.version)) e.version = 'Format semver requis (ex: 1.0.0)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await adminConfigService.updateModule(mod.id, form);
        toast.success('Module mis à jour');
      } else {
        await adminConfigService.createModule(form);
        toast.success('Module créé');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ModuleIcon color="primary" />
        {isEdit ? 'Modifier le module' : 'Nouveau module'}
        <IconButton onClick={onClose} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          {!isEdit && (
            <Grid item xs={12}>
              <TextField
                fullWidth label="Identifiant (snake_case)" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
                error={!!errors.name} helperText={errors.name || 'Ex: payment_system — immutable après création'}
                placeholder="payment_system"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth label="Nom d'affichage" value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              error={!!errors.display_name} helperText={errors.display_name}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} label="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select value={form.status} label="Statut" onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {Object.entries(MODULE_STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Version" value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
              error={!!errors.version} helperText={errors.version || 'Format: 1.0.0'}
            />
          </Grid>
          {!isEdit && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_system} onChange={(e) => setForm({ ...form, is_system: e.target.checked })} />}
                label="Module système (non supprimable)"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Mettre à jour' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Parameter Form Dialog ────────────────────────────────────────────────────

const initialParamForm = {
  module_id: '', key: '', display_name: '', description: '',
  data_type: 'string', default_value: '', current_value: '',
  status: 'active', priority: 'medium', is_required: false,
  is_sensitive: false, display_order: 0
};

const ParameterFormDialog = ({ open, onClose, parameter: param, modules, defaultModuleId, onSaved }) => {
  const isEdit = Boolean(param);
  const [form, setForm] = useState(initialParamForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValue, setShowValue] = useState(false);

  useEffect(() => {
    if (param) {
      setForm({
        module_id: param.module_id || '',
        key: param.key || '',
        display_name: param.display_name || '',
        description: param.description || '',
        data_type: param.data_type || 'string',
        default_value: param.default_value ?? '',
        current_value: param.current_value ?? '',
        status: param.status || 'active',
        priority: param.priority || 'medium',
        is_required: param.is_required || false,
        is_sensitive: param.is_sensitive || false,
        display_order: param.display_order || 0
      });
    } else {
      setForm({ ...initialParamForm, module_id: defaultModuleId || '' });
    }
    setErrors({});
    setShowValue(false);
  }, [param, open, defaultModuleId]);

  const validate = () => {
    const e = {};
    if (!form.module_id) e.module_id = 'Module requis';
    if (!form.display_name.trim()) e.display_name = 'Nom d\'affichage requis';
    if (!isEdit && !form.key.trim()) e.key = 'Clé requise';
    if (!isEdit && !/^[a-z][a-z0-9_]*[a-z0-9]$/.test(form.key)) e.key = 'Format snake_case requis';
    if (!form.data_type) e.data_type = 'Type de donnée requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.default_value === '') payload.default_value = null;
      if (payload.current_value === '') payload.current_value = null;

      if (isEdit) {
        await adminConfigService.updateParameter(param.id, payload);
        toast.success('Paramètre mis à jour');
      } else {
        await adminConfigService.createParameter(payload);
        toast.success('Paramètre créé');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const isReadonly = isEdit && param?.status === 'readonly';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ParamIcon color="primary" />
        {isEdit ? 'Modifier le paramètre' : 'Nouveau paramètre'}
        {isReadonly && <Chip label="Lecture seule" color="info" size="small" icon={<LockIcon />} />}
        <IconButton onClick={onClose} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.module_id}>
              <InputLabel>Module</InputLabel>
              <Select
                value={form.module_id} label="Module"
                onChange={(e) => setForm({ ...form, module_id: e.target.value })}
                disabled={isEdit}
              >
                {modules.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.display_name}</MenuItem>
                ))}
              </Select>
              {errors.module_id && <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>{errors.module_id}</Typography>}
            </FormControl>
          </Grid>
          {!isEdit && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Clé (snake_case)" value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })}
                error={!!errors.key} helperText={errors.key || 'Ex: max_retry_count — immutable'}
                disabled={isEdit}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={isEdit ? 12 : 6}>
            <TextField
              fullWidth label="Nom d'affichage" value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              error={!!errors.display_name} helperText={errors.display_name}
              disabled={isReadonly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} label="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={isReadonly}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth error={!!errors.data_type}>
              <InputLabel>Type de donnée</InputLabel>
              <Select
                value={form.data_type} label="Type de donnée"
                onChange={(e) => setForm({ ...form, data_type: e.target.value })}
                disabled={isEdit}
              >
                {Object.entries(DATA_TYPE_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select value={form.status} label="Statut" onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={isReadonly}>
                {Object.entries(PARAM_STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Priorité</InputLabel>
              <Select value={form.priority} label="Priorité" onChange={(e) => setForm({ ...form, priority: e.target.value })} disabled={isReadonly}>
                {Object.entries(PARAM_PRIORITY_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Valeur par défaut" value={form.default_value}
              onChange={(e) => setForm({ ...form, default_value: e.target.value })}
              disabled={isReadonly}
              type={form.is_sensitive && !showValue ? 'password' : 'text'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Valeur courante" value={form.current_value}
              onChange={(e) => setForm({ ...form, current_value: e.target.value })}
              type={form.is_sensitive && !showValue ? 'password' : 'text'}
              InputProps={{
                endAdornment: form.is_sensitive ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowValue(!showValue)} size="small">
                      {showValue ? <HideIcon /> : <ViewIcon />}
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth type="number" label="Ordre d'affichage" value={form.display_order}
              onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
              disabled={isReadonly}
            />
          </Grid>
          <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={form.is_required} onChange={(e) => setForm({ ...form, is_required: e.target.checked })} disabled={isReadonly} />}
              label="Requis"
            />
          </Grid>
          <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={form.is_sensitive} onChange={(e) => setForm({ ...form, is_sensitive: e.target.checked })} disabled={isReadonly} />}
              label="Sensible"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Mettre à jour' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

const DeleteConfirmDialog = ({ open, onClose, onConfirm, title, message, loading }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <DeleteIcon /> {title}
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>Annuler</Button>
      <Button variant="contained" color="error" onClick={onConfirm} disabled={loading}>
        {loading ? <CircularProgress size={20} /> : 'Supprimer'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Quick Value Edit Dialog ──────────────────────────────────────────────────

const QuickValueDialog = ({ open, onClose, parameter, onSaved }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (parameter) setValue(parameter.current_value ?? parameter.default_value ?? '');
  }, [parameter]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminConfigService.updateParameterValue(parameter.id, value || null);
      toast.success('Valeur mise à jour');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Modifier la valeur</DialogTitle>
      <DialogContent>
        {parameter && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">{parameter.display_name}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 2 }} color="text.disabled">
              Type: {DATA_TYPE_CONFIG[parameter.data_type]?.label || parameter.data_type} • Défaut: {parameter.default_value ?? 'aucun'}
            </Typography>
            <TextField
              fullWidth label="Valeur courante" value={value}
              onChange={(e) => setValue(e.target.value)}
              multiline={parameter.data_type === 'json' || parameter.data_type === 'array'}
              rows={parameter.data_type === 'json' || parameter.data_type === 'array' ? 4 : 1}
              type={parameter.data_type === 'boolean' ? 'text' : parameter.data_type === 'integer' || parameter.data_type === 'float' ? 'number' : 'text'}
            />
            {parameter.data_type === 'boolean' && (
              <Typography variant="caption" color="text.secondary">Valeurs acceptées: true / false</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Modules Tab ──────────────────────────────────────────────────────────────

const ModulesTab = ({ onSelectModule }) => {
  const [modules, setModules] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editMod, setEditMod] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, mod: null, loading: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminConfigService.getModules({
        search: search || undefined,
        status: statusFilter || undefined,
        page: page + 1,
        limit: rowsPerPage
      });
      setModules(res.data || []);
      setTotal(res.total || 0);
    } catch {
      toast.error('Erreur lors du chargement des modules');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    setDeleteDialog((d) => ({ ...d, loading: true }));
    try {
      await adminConfigService.deleteModule(deleteDialog.mod.id);
      toast.success('Module supprimé');
      setDeleteDialog({ open: false, mod: null, loading: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur suppression');
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };

  return (
    <Box>
      {/* Controls */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField
          size="small" placeholder="Rechercher..." value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Statut</InputLabel>
          <Select value={statusFilter} label="Statut" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">Tous</MenuItem>
            {Object.entries(MODULE_STATUS_CONFIG).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">Actualiser</Button>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditMod(null); setFormOpen(true); }}>
          Nouveau module
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Identifiant</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Paramètres</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Système</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && modules.length === 0 ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((__, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : modules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Aucun module trouvé
                </TableCell>
              </TableRow>
            ) : (
              modules.map((mod) => (
                <TableRow key={mod.id} hover sx={{ cursor: 'pointer' }} onClick={() => onSelectModule(mod)}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{mod.display_name}</Typography>
                      {mod.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 250, display: 'block' }}>
                          {mod.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                      {mod.name}
                    </Typography>
                  </TableCell>
                  <TableCell><StatusChip status={mod.status} type="module" /></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">v{mod.version}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${mod.activeParameterCount}/${mod.parameterCount}`}
                      size="small"
                      color={mod.activeParameterCount > 0 ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {mod.is_system && <Chip label="Système" size="small" color="secondary" />}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => { setEditMod(mod); setFormOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!mod.is_system && (
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, mod, loading: false })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Lignes:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
        />
      </TableContainer>

      {/* Dialogs */}
      <ModuleFormDialog
        open={formOpen} onClose={() => setFormOpen(false)}
        module={editMod} onSaved={load}
      />
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, mod: null, loading: false })}
        onConfirm={handleDelete}
        loading={deleteDialog.loading}
        title="Supprimer le module"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteDialog.mod?.display_name}" ? Cette action est irréversible.`}
      />
    </Box>
  );
};

// ─── Parameters Tab ───────────────────────────────────────────────────────────

const ParametersTab = ({ defaultModuleId = null }) => {
  const [params, setParams] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState(defaultModuleId || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dataTypeFilter, setDataTypeFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editParam, setEditParam] = useState(null);
  const [quickEdit, setQuickEdit] = useState({ open: false, param: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, param: null, loading: false });
  const [showFilters, setShowFilters] = useState(false);

  const loadModules = useCallback(async () => {
    try {
      const res = await adminConfigService.getModules({ limit: 200 });
      setModules(res.data || []);
    } catch { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminConfigService.getParameters({
        search: search || undefined,
        module_id: moduleFilter || undefined,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        data_type: dataTypeFilter || undefined,
        page: page + 1,
        limit: rowsPerPage
      });
      setParams(res.data || []);
      setTotal(res.total || 0);
    } catch {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, [search, moduleFilter, statusFilter, priorityFilter, dataTypeFilter, page, rowsPerPage]);

  useEffect(() => { loadModules(); }, [loadModules]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setModuleFilter(defaultModuleId || ''); setPage(0); }, [defaultModuleId]);

  const handleDelete = async () => {
    setDeleteDialog((d) => ({ ...d, loading: true }));
    try {
      await adminConfigService.deleteParameter(deleteDialog.param.id);
      toast.success('Paramètre supprimé');
      setDeleteDialog({ open: false, param: null, loading: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur suppression');
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };

  const clearFilters = () => {
    setSearch(''); setModuleFilter(''); setStatusFilter('');
    setPriorityFilter(''); setDataTypeFilter(''); setPage(0);
  };

  const hasActiveFilters = search || moduleFilter || statusFilter || priorityFilter || dataTypeFilter;

  return (
    <Box>
      {/* Controls */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }} flexWrap="wrap" alignItems="center">
        <TextField
          size="small" placeholder="Rechercher clé, nom…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Module</InputLabel>
          <Select value={moduleFilter} label="Module" onChange={(e) => { setModuleFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">Tous les modules</MenuItem>
            {modules.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.display_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Filtres avancés">
          <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
            <Button
              size="small" variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtres
            </Button>
          </Badge>
        </Tooltip>
        {hasActiveFilters && (
          <Button size="small" onClick={clearFilters} startIcon={<CloseIcon />}>
            Réinitialiser
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">Actualiser</Button>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditParam(null); setFormOpen(true); }}>
          Nouveau paramètre
        </Button>
      </Stack>

      {/* Advanced Filters */}
      <Collapse in={showFilters}>
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Statut</InputLabel>
              <Select value={statusFilter} label="Statut" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(PARAM_STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Priorité</InputLabel>
              <Select value={priorityFilter} label="Priorité" onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Toutes</MenuItem>
                {Object.entries(PARAM_PRIORITY_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type de donnée</InputLabel>
              <Select value={dataTypeFilter} label="Type de donnée" onChange={(e) => { setDataTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(DATA_TYPE_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      </Collapse>

      {/* Active filters summary */}
      {hasActiveFilters && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Filtres actifs: {[
              search && `Recherche: "${search}"`,
              moduleFilter && `Module: ${modules.find((m) => m.id === moduleFilter)?.display_name}`,
              statusFilter && `Statut: ${PARAM_STATUS_CONFIG[statusFilter]?.label}`,
              priorityFilter && `Priorité: ${PARAM_PRIORITY_CONFIG[priorityFilter]?.label}`,
              dataTypeFilter && `Type: ${DATA_TYPE_CONFIG[dataTypeFilter]?.label}`
            ].filter(Boolean).join(' • ')}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Paramètre</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Valeur courante</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Priorité</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && params.length === 0 ? (
              [...Array(8)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(8)].map((__, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : params.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Aucun paramètre trouvé
                </TableCell>
              </TableRow>
            ) : (
              params.map((p) => {
                const displayValue = p.is_sensitive ? '••••••••' : (p.current_value ?? p.default_value ?? '—');
                const isCritical = p.priority === 'critical';
                return (
                  <TableRow
                    key={p.id} hover
                    sx={{ borderLeft: isCritical ? '3px solid' : undefined, borderLeftColor: isCritical ? 'error.main' : undefined }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isCritical && <CriticalIcon fontSize="small" color="error" />}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{p.display_name}</Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {p.key}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap>{p.module?.display_name || '—'}</Typography>
                    </TableCell>
                    <TableCell><DataTypeChip dataType={p.data_type} /></TableCell>
                    <TableCell>
                      <Tooltip title={!p.is_sensitive ? (p.current_value ?? p.default_value ?? 'non défini') : 'Valeur masquée'}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            maxWidth: 180,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: p.current_value == null ? 'text.disabled' : 'text.primary'
                          }}
                        >
                          {String(displayValue).length > 30 ? String(displayValue).substring(0, 30) + '…' : displayValue}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell><PriorityChip priority={p.priority} /></TableCell>
                    <TableCell><StatusChip status={p.status} type="param" /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {p.is_required && <Chip label="Requis" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                        {p.is_sensitive && <Chip label="Sensible" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end">
                        {p.status !== 'readonly' && (
                          <Tooltip title="Modifier la valeur rapidement">
                            <IconButton size="small" color="primary" onClick={() => setQuickEdit({ open: true, param: p })}>
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => { setEditParam(p); setFormOpen(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {p.status !== 'readonly' && (
                          <Tooltip title="Supprimer">
                            <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, param: p, loading: false })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Lignes:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
        />
      </TableContainer>

      {/* Dialogs */}
      <ParameterFormDialog
        open={formOpen} onClose={() => setFormOpen(false)}
        parameter={editParam} modules={modules}
        defaultModuleId={moduleFilter}
        onSaved={load}
      />
      <QuickValueDialog
        open={quickEdit.open} onClose={() => setQuickEdit({ open: false, param: null })}
        parameter={quickEdit.param} onSaved={load}
      />
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, param: null, loading: false })}
        onConfirm={handleDelete}
        loading={deleteDialog.loading}
        title="Supprimer le paramètre"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteDialog.param?.display_name}" ?`}
      />
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminConfigPage = () => {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const res = await adminConfigService.getStats();
        setStats(res.data);
      } catch { /* silent */ } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleSelectModule = (mod) => {
    setSelectedModule(mod);
    setTab(1);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <SettingsIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>
            Gestion de la Configuration Système
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Administrez les modules et paramètres de configuration de la plateforme
        </Typography>
      </Box>

      {/* Stats */}
      <StatsCards stats={stats} loading={statsLoading} />

      {/* Module breadcrumb when filtering params by module */}
      {tab === 1 && selectedModule && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => setSelectedModule(null)}>
              Voir tout
            </Button>
          }
        >
          Affichage des paramètres du module : <strong>{selectedModule.display_name}</strong>
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <ModuleIcon fontSize="small" />
                <span>Modules</span>
                {stats && (
                  <Chip
                    label={stats.modules.total}
                    size="small" color="primary" variant="outlined"
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <ParamIcon fontSize="small" />
                <span>Paramètres</span>
                {stats && (
                  <Chip
                    label={stats.parameters.total}
                    size="small" color="primary" variant="outlined"
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                )}
                {selectedModule && (
                  <Chip
                    label={selectedModule.display_name} size="small" color="secondary"
                    sx={{ height: 18, fontSize: '0.7rem' }}
                    onDelete={() => setSelectedModule(null)}
                  />
                )}
              </Stack>
            }
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && <ModulesTab onSelectModule={handleSelectModule} />}
          {tab === 1 && <ParametersTab defaultModuleId={selectedModule?.id || null} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminConfigPage;
